import {
  ArrowLeft,
  Bell,
  Database,
  FileText,
  Globe,
  Mail,
  MessageSquare,
  Palette,
  Settings,
  Shield,
  Users,
  Zap,
} from 'lucide-react';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface SettingsMenuItem {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  status?: 'configured' | 'needs-attention' | 'not-configured';
}

const SettingsSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems: SettingsMenuItem[] = [
    {
      id: 'system',
      name: 'System Configuration',
      description: 'Global preferences & regional settings',
      icon: Globe,
      href: '/settings/system',
      status: 'configured',
    },
    {
      id: 'email-templates',
      name: 'Email Templates',
      description: 'Email templates & delivery settings',
      icon: Mail,
      href: '/settings/templates/email',
      status: 'needs-attention',
    },
    {
      id: 'sms-templates',
      name: 'SMS Templates',
      description: 'SMS message templates',
      icon: MessageSquare,
      href: '/settings/templates/sms',
      status: 'configured',
    },
    {
      id: 'notification-templates',
      name: 'Notification Templates',
      description: 'In-app notification settings',
      icon: Bell,
      href: '/settings/templates/notifications',
      status: 'configured',
    },
    {
      id: 'pdf-templates',
      name: 'PDF Templates',
      description: 'Customize PDF document output',
      icon: FileText,
      href: '/settings/templates/pdf',
      status: 'configured',
    },
    {
      id: 'dropdowns',
      name: 'Dropdown Management',
      description: 'System dropdowns & categories',
      icon: Database,
      href: '/settings/dropdowns',
      status: 'configured',
    },
    {
      id: 'security',
      name: 'Security & Auth',
      description: 'Security policies & access controls',
      icon: Shield,
      href: '/settings/security',
      status: 'needs-attention',
    },
    {
      id: 'user-defaults',
      name: 'User Defaults',
      description: 'Default preferences for new users',
      icon: Users,
      href: '/settings/user-defaults',
      status: 'configured',
    },
    {
      id: 'branding',
      name: 'Branding & Appearance',
      description: 'Logos, colors & themes',
      icon: Palette,
      href: '/settings/branding',
      status: 'not-configured',
    },
    {
      id: 'integrations',
      name: 'Integrations & APIs',
      description: 'Third-party integrations',
      icon: Zap,
      href: '/settings/integrations',
      status: 'configured',
    },
    {
      id: 'documents',
      name: 'Document Settings',
      description: 'Document templates & workflows',
      icon: FileText,
      href: '/settings/documents',
      status: 'configured',
    },
  ];

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div
      className="w-72 bg-white border-r border-gray-200 flex flex-col h-full"
      data-testid="settings-sidebar"
    >
      {/* Sidebar Header with Back Button */}
      <div className="p-4 border-b border-gray-200">
        <button
          onClick={handleBackToDashboard}
          className="flex items-center text-sm text-gray-600 hover:text-brand-primary transition-colors mb-3 group"
        >
          <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Dashboard
        </button>
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Settings className="h-5 w-5 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Settings</h2>
            <p className="text-xs text-gray-500">System configuration</p>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto p-3">
        <ul className="space-y-1">
          {menuItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.href);

            return (
              <li key={item.id}>
                <Link
                  to={item.href}
                  className={`
                    group flex items-start w-full px-3 py-2.5 text-sm rounded-lg transition-all duration-200
                    ${
                      active
                        ? 'bg-brand-50 border-l-4 border-brand-primary -ml-[2px] pl-[10px]'
                        : 'hover:bg-gray-50'
                    }
                  `}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon
                    className={`
                      h-5 w-5 mr-3 mt-0.5 flex-shrink-0 transition-colors
                      ${active ? 'text-brand-primary' : 'text-gray-400 group-hover:text-brand-primary'}
                    `}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span
                        className={`font-medium truncate ${
                          active ? 'text-brand-primary' : 'text-gray-700 group-hover:text-gray-900'
                        }`}
                      >
                        {item.name}
                      </span>
                      {item.status === 'needs-attention' && (
                        <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0 ml-2"></span>
                      )}
                      {item.status === 'not-configured' && (
                        <span className="w-2 h-2 bg-gray-300 rounded-full flex-shrink-0 ml-2"></span>
                      )}
                    </div>
                    <p
                      className={`text-xs mt-0.5 truncate ${
                        active ? 'text-brand-primary/70' : 'text-gray-500'
                      }`}
                    >
                      {item.description}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Sidebar Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            <span>Configured</span>
          </div>
          <span>•</span>
          <div className="flex items-center space-x-1">
            <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
            <span>Needs attention</span>
          </div>
          <span>•</span>
          <div className="flex items-center space-x-1">
            <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
            <span>Not set</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsSidebar;
